/**
 * CommandHero — Marketing-Secrets-inspired hero block for the unified
 * Command Center / AI Studio landing.
 *
 * Two operating modes:
 *  1) **Standalone (default)**: no props passed. Submitting routes to
 *     `/ai-hub?prefill=1` via sessionStorage. Used as a legacy landing.
 *  2) **Controlled**: when `value`/`onChange`/`onSubmit` are provided, the
 *     component is fully driven by its parent (used inside AIHub `heroMode`).
 *     The parent also supplies the model picker via the `modelPicker` slot so
 *     users see the FULL OpenRouter dropdown (search, free vs paid, 200+ models)
 *     instead of a placeholder chip.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Textarea } from './ui/textarea';
import {
  Send, Mic, MicOff, Radio, Loader2, Paperclip, Sparkles,
  Search, Mail, BarChart3, Calendar, ListChecks,
  TrendingUp, FileText, ChevronDown, Receipt,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { CBLogo } from './CBLogo';
import useVoiceTranscribe from '../hooks/useVoiceTranscribe';

const ACTION_CHIPS = [
  { key: 'analyze',   label: 'Analyze cash flow', icon: BarChart3, prompt: 'Analyze my recent cash flow and tell me where my money is going. Suggest 3 ways to improve it.' },
  { key: 'invoice',   label: 'Draft invoice',     icon: FileText,  prompt: 'Draft a professional invoice for a new client. Use placeholders for client name, amount, services, and due date.' },
  { key: 'tax',       label: 'Tax planning',      icon: Receipt,   prompt: 'I want to plan ahead for taxes. Walk me through 5 strategies to legally reduce my tax bill for the year.' },
  { key: 'reconcile', label: 'Reconcile help',    icon: ListChecks,prompt: 'Walk me through how to reconcile my bank statements step-by-step. Make it simple.' },
  { key: 'forecast',  label: 'Forecast revenue',  icon: TrendingUp,prompt: 'Help me forecast my revenue for the next 90 days based on my current pipeline. What inputs do you need?' },
];

const SUGGESTIONS = [
  { key: 's1', icon: Search,    title: 'What 5 expenses am I paying that I could cut without hurting the business?' },
  { key: 's2', icon: Mail,      title: 'Write a friendly payment reminder email for an invoice that\'s 14 days overdue.' },
  { key: 's3', icon: BarChart3, title: 'Compare my last 3 months of revenue and explain any trends you see.' },
  { key: 's4', icon: Calendar, title: 'Draft a 4-week onboarding plan for a new bookkeeping client.' },
];

export default function CommandHero({
  // Controlled-mode props. When `value` is undefined we run in standalone mode.
  value,
  onChange,
  onSubmit,
  // Optional slot for parent-provided model picker. Falls back to a static chip.
  modelPicker,
  sending = false,
}) {
  const navigate = useNavigate();
  const [localInput, setLocalInput] = useState('');
  const isControlled = value !== undefined;
  const input = isControlled ? value : localInput;
  const setInput = (v) => {
    if (isControlled) onChange?.(v);
    else setLocalInput(v);
  };

  const send = (prompt) => {
    const txt = (prompt || input || '').trim();
    if (!txt || sending) return;
    if (isControlled) {
      // Make sure the textarea reflects any chip/suggestion text before submit,
      // and the parent's onSubmit handles the actual send + thread creation.
      onChange?.(txt);
      // Defer one tick so the controlled state has settled before submit.
      Promise.resolve().then(() => onSubmit?.(txt));
    } else {
      try { sessionStorage.setItem('ai_studio_prefill_prompt', txt); }
      catch { /* ignore quota errors */ }
      navigate('/ai-hub?prefill=1');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Voice → text. Appends transcribed text to the existing input so the user
  // can compose by voice + keyboard interchangeably. Continuous mode auto-
  // chunks every ~6 seconds for live dictation.
  const voice = useVoiceTranscribe({
    onText: (txt) => setInput(input ? `${input} ${txt}` : txt),
  });
  const toggleMic = () => (voice.recording ? voice.stop() : voice.start({ continuous: false }));
  const toggleContinuous = () =>
    voice.recording ? voice.stop() : voice.start({ continuous: true });

  return (
    <section
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 via-emerald-50/30 to-amber-50/30 dark:from-zinc-950 dark:via-emerald-950/20 dark:to-amber-950/10 border px-6 py-10 sm:px-10 sm:py-14"
      data-testid="command-hero"
    >
      {/* Decorative dots in the background */}
      <div className="absolute inset-0 -z-0 opacity-30 pointer-events-none [background-image:radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.08)_1px,transparent_0)] dark:[background-image:radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)] [background-size:20px_20px]" />

      <div className="relative z-10 max-w-3xl mx-auto">
        {/* Title */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <CBLogo size="md" />
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-400">
              CountBucks AI
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 dark:text-zinc-100 leading-tight">
            What can I help you <span className="bg-gradient-to-r from-emerald-600 to-amber-600 dark:from-emerald-400 dark:to-amber-400 bg-clip-text text-transparent">build today?</span>
          </h1>
        </div>

        {/* Prompt input */}
        <div className="rounded-2xl border-2 border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 shadow-xl shadow-emerald-500/5 focus-within:border-emerald-500/60 transition-all">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything — accounting, strategy, drafts, forecasts…"
            rows={2}
            className="resize-none border-0 bg-transparent text-base placeholder-zinc-400 dark:placeholder-zinc-500 focus-visible:ring-0 min-h-[80px] px-4 pt-3.5"
            data-testid="command-hero-input"
            disabled={sending}
          />
          <div className="flex items-center gap-1.5 px-3 pb-2.5 border-t border-zinc-100 dark:border-white/5 pt-1.5">
            {/* Model picker slot: parent-provided full dropdown in heroMode,
              * static chip when used standalone. */}
            {modelPicker ? (
              <div data-testid="command-hero-model-slot">{modelPicker}</div>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/ai-hub')}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-xs text-zinc-700 dark:text-zinc-300"
                title="Choose model in AI Studio"
                data-testid="command-hero-model-chip"
              >
                <Sparkles className="w-3 h-3" /> AI Studio <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
            )}
            <button
              type="button"
              onClick={toggleMic}
              disabled={voice.busy && !voice.recording}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                voice.recording && !voice.continuous
                  ? 'bg-red-500 text-white animate-pulse'
                  : voice.busy
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    : 'hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500',
              )}
              title={voice.recording ? 'Stop recording' : 'Tap to speak (one-shot)'}
              data-testid="command-hero-mic"
            >
              {voice.busy && !voice.recording ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : voice.recording && !voice.continuous ? (
                <MicOff className="w-3.5 h-3.5" />
              ) : (
                <Mic className="w-3.5 h-3.5" />
              )}
            </button>
            {/* Continuous live-dictation toggle. Chunks every ~6s into Whisper
              * so the user sees their words flow in mid-speech. Shows a steady
              * red "radio" icon while live to differentiate from one-shot. */}
            <button
              type="button"
              onClick={toggleContinuous}
              disabled={voice.busy && !voice.recording}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                voice.recording && voice.continuous
                  ? 'bg-red-500 text-white shadow-[0_0_0_3px_rgba(239,68,68,0.25)]'
                  : 'hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500',
              )}
              title={voice.continuous ? 'Stop live dictation' : 'Live dictation (continuous)'}
              data-testid="command-hero-mic-live"
            >
              <Radio className={cn('w-3.5 h-3.5', voice.recording && voice.continuous && 'animate-pulse')} />
            </button>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500"
              title="Attach"
              data-testid="command-hero-attach"
            >
              <Paperclip className="w-3.5 h-3.5" />
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => send()}
              disabled={!input.trim() || sending}
              className="w-9 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white flex items-center justify-center disabled:opacity-40 transition-colors"
              data-testid="command-hero-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
        <p className="text-center text-[11px] text-muted-foreground mt-2">
          Enter to send · Shift+Enter for new line
        </p>

        {/* Action chips */}
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
          {ACTION_CHIPS.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setInput(c.prompt)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all',
                  'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-white/10 text-zinc-700 dark:text-zinc-300',
                  'hover:border-emerald-500/40 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/30 hover:text-emerald-700 dark:hover:text-emerald-300 hover:scale-[1.03]',
                )}
                data-testid={`command-hero-chip-${c.key}`}
              >
                <Icon className="w-3 h-3" />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Suggested for you */}
        <div className="mt-8">
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-3">
            Suggested for you
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SUGGESTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => send(s.title)}
                  className="group flex items-start gap-2.5 text-left rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/40 hover:border-emerald-500/40 hover:bg-emerald-50/30 dark:hover:bg-emerald-950/20 p-3 transition-all"
                  data-testid={`command-hero-suggestion-${s.key}`}
                >
                  <div className="w-7 h-7 rounded-md bg-emerald-500/10 dark:bg-emerald-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/25">
                    <Icon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">{s.title}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
